import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  inject,
  OnInit,
  signal,
  ViewChild,
  computed,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyButtonGroupModule } from '@vendasta/galaxy/button-group';
import { AiKnowledgeService } from '../../ai-knowledge.service';
import { FileDownloadService } from '../../services/file-download.service';
import { Environment, EnvironmentService } from '@galaxy/core';

import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatInputModule } from '@angular/material/input';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ApplicationSelectorComponent } from '../../application-selector/application-selector.component';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { KnowledgeSourceWithSuggestedFlag } from '../../ai-knowledge-util';

import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { WebChatWidgetService } from '../../services/web-chat-widget.service';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { KnowledgeSourceConfig, KnowledgeSourceKey } from '@vendasta/embeddings';
import { KnowledgeCreatedEvent, StepChangeEvent } from '../common/interfaces';
import {
  FileInfo,
  FileUploadError,
  FileUploadStatus,
  GalaxyUploaderModule,
  GalaxyUploaderService,
  GALAXY_UPLOADER_SERVICE_TOKEN,
} from '@vendasta/galaxy/uploader';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileTypeIconService } from '../../services/file-type-icon.service';

interface FileUploadState {
  file: FileInfo | null;
  jwtToken: string | null;
  isUploading: boolean;
  hasBeenSaved: boolean;
  hasExistingFile: boolean;
}

interface Data {
  knowledgeSourceKey?: KnowledgeSourceKey;
  excludeAddToAIStep?: boolean;
  currentAppId?: string;
}

@Component({
  selector: 'ai-knowledge-file-upload',
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatInputModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    GalaxyButtonGroupModule,
    GalaxySnackbarModule,
    MatStepperModule,
    MatIconModule,
    MatTooltipModule,
    ApplicationSelectorComponent,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    GalaxyUploaderModule,
  ],
  providers: [
    AiKnowledgeService,
    WebChatWidgetService,
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
    {
      provide: GALAXY_UPLOADER_SERVICE_TOKEN,
      useClass: GalaxyUploaderService,
    },
  ],
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit, KnowledgeCreatedEvent, StepChangeEvent {
  @ViewChild('stepper') stepper: MatStepper;
  @ViewChild('applicationSelector') applicationSelector: ApplicationSelectorComponent;

  private readonly snackbarService = inject(SnackbarService);
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly webChatWidgetService = inject(WebChatWidgetService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly translateService = inject(TranslateService);

  private readonly environmentService = inject(EnvironmentService);
  private readonly fileTypeIconService = inject(FileTypeIconService);

  readonly otherAIDisclaimer = signal('');
  readonly isLoading = signal(true);

  // a string corresponding to the current step should be emitted whenever a step changes
  // this will be used to update the address to the current step, so that it can be linked to directly
  stepChange = new EventEmitter<string>();
  excludeAddToAIStep: boolean;
  // a list of strings corresponding to the step indexes for a stepper
  steps: string[] = [''];
  // the current step
  selectedStep = signal(0);

  form: FormGroup;
  knowledgeSource: KnowledgeSourceWithSuggestedFlag;
  knowledgeCreated = new EventEmitter<string>();

  // File upload related properties
  uploadUrl: string;
  private readonly uploadHosts = new Map<Environment, string>([
    [Environment.DEMO, 'https://embeddings-demo.apigateway.co'],
    [Environment.PROD, 'https://embeddings-prod.apigateway.co'],
    [Environment.LOCAL, 'https://embeddings-demo.apigateway.co'], // Use demo for local development
    [Environment.TEST, 'https://embeddings-demo.apigateway.co'], // Use demo for test
  ]);
  maxFiles = 1;
  maxFileSize = 100 * 1024 * 1024; // 100MB
  acceptedFileTypes =
    '.csv,.docx,.html,.htm,.jpg,.jpeg,.png,.pdf,.txt,.text,.md,.markdown,.json,.jsonl,.pptx,.xlsx,.xls';

  // Consolidated file state
  fileState = signal<FileUploadState>({
    file: null,
    jwtToken: null,
    isUploading: false,
    hasBeenSaved: false,
    hasExistingFile: false,
  });

  // Computed properties for template convenience and derived state
  readonly uploadedFile = computed(() => this.fileState().file);
  readonly hasExistingFileOnly = computed(() => this.knowledgeSource?.config?.fileConfig && !this.fileState().file);

  // Current file display information computed signal
  readonly currentFileInfo = computed(() => {
    const fileConfig = this.knowledgeSource?.config?.fileConfig;
    if (!fileConfig) {
      return null;
    }

    const mimetype = fileConfig.mimetype || '';
    const fileName = fileConfig.fileName || 'Unknown file';
    const fileUrl = fileConfig.fileUrl || '';

    // Convert mimetype to readable format
    const typeMap: { [key: string]: string } = {
      'application/pdf': 'PDF',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'application/msword': 'DOC',
      'text/plain': 'TXT',
      'text/html': 'HTML',
      'text/csv': 'CSV',
      'application/json': 'JSON',
      'text/markdown': 'Markdown',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
      'application/vnd.ms-excel': 'XLS',
      'image/jpeg': 'JPEG',
      'image/png': 'PNG',
      'image/jpg': 'JPG',
    };

    const displayType = typeMap[mimetype] || mimetype.split('/')[1]?.toUpperCase() || 'Unknown';
    const icon = this.fileTypeIconService.getIconForMimetype(mimetype);
    const downloadUrl = fileUrl ? this.fileDownloadService.getDownloadLink(fileUrl) : null;
    const viewUrl = fileUrl ? this.fileDownloadService.getViewLink(fileUrl) : null;

    return {
      fileName,
      mimetype,
      displayType,
      icon,
      fileUrl,
      downloadUrl,
      viewUrl,
    };
  });

  // Validation computed signals
  readonly hasValidFile = computed(() => {
    const state = this.fileState();
    const hasUploadedFile = state.file?.status === FileUploadStatus.Success;
    const noFileUploading = !state.isUploading;
    const hasJwtToken = !!state.jwtToken;
    const hasExistingFile = state.hasExistingFile;

    // Valid if there's either a new uploaded file OR an existing file
    return (hasUploadedFile && noFileUploading && hasJwtToken) || hasExistingFile;
  });

  // Signal to track form validity reactively
  private readonly formValiditySignal = signal(false);

  readonly isFormValidForCurrentStep = computed(() => {
    const currentStep = this.selectedStep();
    switch (currentStep) {
      case 0: {
        // Use the reactive form validity signal instead of direct property access
        const hasValidName = this.formValiditySignal();
        const hasValidFile = this.hasValidFile();
        return !!(hasValidName && hasValidFile);
      }
      case 1: {
        return true;
      }
      default:
        return false;
    }
  });

  readonly currentValidationErrors = computed(() => {
    const currentStep = this.selectedStep();
    const errors: string[] = [];

    if (currentStep === 0) {
      const hasValidName = this.formValiditySignal();
      const nameControl = this.form?.get('name');
      const state = this.fileState();

      // Check name validation - only show error if field is empty
      if (!hasValidName && !nameControl?.value?.trim()) {
        errors.push('EDIT_KNOWLEDGE.FIELD_REQUIRED');
      }

      // Check file validation
      const hasUploadedFile = state.file?.status === FileUploadStatus.Success;
      const hasExistingFile = state.hasExistingFile;

      if (!hasUploadedFile && !hasExistingFile) {
        errors.push('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.NO_FILE_UPLOADED');
      } else if (hasUploadedFile && state.isUploading) {
        errors.push('COMMON.UPLOADING');
      } else if (hasUploadedFile && !state.jwtToken) {
        errors.push('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.UPLOAD_PROCESSING');
      }
    }

    return errors;
  });

  readonly hasUnsavedChanges = computed(() => {
    const currentStep = this.selectedStep();
    switch (currentStep) {
      case 0: {
        const state = this.fileState();
        // If we've successfully saved, then there are no unsaved changes
        if (state.hasBeenSaved) {
          return false;
        }
        return this.form?.dirty || state.file !== null;
      }
      case 1: {
        return false;
      }
      default:
        return false;
    }
  });

  // Stepper computed signals
  readonly isOnLastStep = computed(() => this.selectedStep() === this.steps.length - 1);
  readonly shouldShowBack = computed(() => this.selectedStep() > 0);
  readonly submitActionText = computed(() => {
    return this.isOnLastStep() ? 'COMMON.SAVE' : 'COMMON.NEXT';
  });

  constructor(
    readonly destroyRef: DestroyRef,
    public dialogRef: MatDialogRef<FileUploadComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Data,
    @Inject(GALAXY_UPLOADER_SERVICE_TOKEN) private uploaderService: GalaxyUploaderService,
    private fileDownloadService: FileDownloadService,
  ) {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required]),
    });
  }

  async ngOnInit(): Promise<void> {
    this.dialogRef.disableClose = true;

    // Initialize upload URL based on environment
    const environment = this.environmentService.getEnvironment();
    const baseUrl = this.uploadHosts.get(environment) || this.uploadHosts.get(Environment.DEMO);
    this.uploadUrl = `${baseUrl}/api/v1/upload-file`;

    if (this.data) {
      if (this.data?.knowledgeSourceKey) {
        await this.knowledgeService
          .getKnowledgeSource(this.data.knowledgeSourceKey.knowledgeSourceId, this.data.knowledgeSourceKey.namespace)
          .then(
            (knowledgeSource) => {
              this.knowledgeSource = knowledgeSource as KnowledgeSourceWithSuggestedFlag;
            },
            (_) => {
              this.knowledgeSource = null;
            },
          );

        this.otherAIDisclaimer.set(
          await this.knowledgeService.getOtherAIDisclaimerText(
            this.data.knowledgeSourceKey.knowledgeSourceId,
            this.data.currentAppId,
          ),
        );
      }

      this.excludeAddToAIStep = this.data.excludeAddToAIStep;
    }

    if (!this.excludeAddToAIStep) {
      this.steps.push('appSelection');
    }

    this.form = new FormGroup(
      {
        name: new FormControl(this.knowledgeSource?.name || '', [Validators.required]),
      },
      { validators: [this.fileUploadValidator.bind(this)] },
    );

    // Initialize file state based on existing knowledge source
    if (this.knowledgeSource) {
      const hasExistingFile = !!this.knowledgeSource?.config?.fileConfig?.fileUrl;
      this.updateFileState({
        hasExistingFile,
        hasBeenSaved: true, // Existing knowledge source is considered "saved"
      });
    }

    // Reset saved state when form changes and update form validity signal
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateFileState({ hasBeenSaved: false });
      this.updateFormValiditySignal();
    });

    // Also listen to status changes (validity changes)
    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateFormValiditySignal();
    });

    // Initial form validity update
    this.updateFormValiditySignal();

    this.isLoading.set(false);
  }

  onFileUploaded(fileInfo: FileInfo): void {
    this.handleSuccessfulUpload(fileInfo);
    this.autoFillNameFromFile(fileInfo.name);
    this.snackbarService.openSuccessSnack('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.UPLOAD_SUCCESS');
    this.updateFormValidation();
  }

  onFileUploadError(error: FileUploadError): void {
    if (this.isReplaceableError(error)) {
      this.handleFileReplacement(error.fileInfo);
      return;
    }

    this.snackbarService.openErrorSnack('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.UPLOAD_ERROR');
    this.updateFileState({ jwtToken: null });
  }

  onFilesChanged(files: FileInfo[]): void {
    this.updateFileFromChanges(files);
    this.updateFormValidation();
  }

  // Helper methods for file upload operations
  private handleSuccessfulUpload(fileInfo: FileInfo): void {
    const jwtToken = fileInfo.resp?.data?.url || null;
    this.updateFileState({
      file: fileInfo,
      jwtToken,
      hasBeenSaved: false,
    });
  }

  private autoFillNameFromFile(fileName: string | undefined): void {
    if (fileName) {
      const baseName = fileName.replace(/\.[^/.]+$/, ''); // Remove extension
      this.form.get('name')?.setValue(baseName);
    }
  }

  private isReplaceableError(error: FileUploadError): boolean {
    return (
      error.error?.message === 'GALAXY.UPLOADER.ERROR.DUPLICATE_FILE_ATTEMPT' ||
      error.error?.message === 'GALAXY.UPLOADER.ERROR.MAX_FILES_EXCEEDED'
    );
  }

  private handleFileReplacement(fileInfo: FileInfo): void {
    this.uploaderService.clear();
    this.resetFileState();
    this.snackbarService.openSuccessSnack('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.FILE_REPLACED');
    this.updateFormValidation();

    // Re-add the file after clearing
    setTimeout(() => {
      this.uploaderService.addFile(fileInfo.file, fileInfo.context);
    }, 100);
  }

  private updateFileFromChanges(files: FileInfo[]): void {
    const file = files.length > 0 ? files[0] : null;
    const isUploading = file?.status === FileUploadStatus.InProgress;

    this.updateFileState({
      file,
      isUploading,
      jwtToken: !file || file.status !== FileUploadStatus.Success ? null : this.fileState().jwtToken,
      hasBeenSaved: false,
    });
  }

  private resetFileState(): void {
    this.updateFileState({
      file: null,
      isUploading: false,
      jwtToken: null,
      hasBeenSaved: false,
    });
  }

  private updateFormValidation(): void {
    this.form.updateValueAndValidity();
    this.updateFormValiditySignal();
  }

  private updateFormValiditySignal(): void {
    const nameControl = this.form?.get('name');
    const isNameValid = nameControl?.valid ?? false;
    this.formValiditySignal.set(isNameValid);
  }

  private updateFileState(updates: Partial<FileUploadState>): void {
    this.fileState.update((current) => ({ ...current, ...updates }));
  }

  private fileUploadValidator(): { [key: string]: any } | null {
    return this.hasValidFile() ? null : { fileRequired: true };
  }

  async submit(): Promise<void> {
    if (!this.isFormValid()) {
      this.snackbarService.openErrorSnack('COMMON.INVALID_FORM');
      return;
    }

    const currentStep = this.selectedStep();
    switch (currentStep) {
      case 0:
        await this.handleStepZeroSubmit();
        break;
      case 1:
        await this.handleStepOneSubmit();
        break;
    }
  }

  private async handleStepZeroSubmit(): Promise<void> {
    const name = this.form.get('name')?.value;
    const state = this.fileState();
    const hasNewFile = !!state.jwtToken;
    const hasExistingFile = state.hasExistingFile;

    // Validate file requirements
    if (!hasNewFile && !hasExistingFile) {
      this.snackbarService.openErrorSnack('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.NO_FILE_UPLOADED');
      return;
    }

    const ksId = hasNewFile ? await this.handleNewFileUpload(name, state) : await this.handleNameOnlyUpdate(name);

    if (ksId) {
      await this.handleSuccessfulSave(ksId);
    }
  }

  private async handleStepOneSubmit(): Promise<void> {
    await this.applicationSelector.setAppsForKnowledgeSource();
    this.close();
  }

  private async handleNewFileUpload(name: string, state: FileUploadState): Promise<string | null> {
    const config = this.createFileConfig(state);
    const filename = state.file?.name || 'Unknown file';
    const description = `Created from ${filename}`;

    if (this.knowledgeSource?.id) {
      // Update existing knowledge source with new file
      this.knowledgeSource.name = name;
      this.knowledgeSource.description = description;
      this.knowledgeSource.config = config as KnowledgeSourceConfig;
      await this.knowledgeService.updateKnowledgeSourceNameAndConfig(this.knowledgeSource);
      return this.knowledgeSource.id;
    } else {
      // Create new knowledge source
      return await this.knowledgeService.createKnowledgeSourceWithDescription(name, description, config);
    }
  }

  private async handleNameOnlyUpdate(name: string): Promise<string | null> {
    if (this.knowledgeSource?.id) {
      this.knowledgeSource.name = name;
      await this.knowledgeService.updateKnowledgeSourceNameAndConfig(this.knowledgeSource);
      return this.knowledgeSource.id;
    } else {
      // This shouldn't happen - we can't have an existing file without an existing knowledge source
      this.snackbarService.openErrorSnack('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.NO_FILE_UPLOADED');
      return null;
    }
  }

  private createFileConfig(state: FileUploadState) {
    const mimetype = state.file?.file?.type || 'application/octet-stream';
    const filename = state.file?.name || 'Unknown file';

    return {
      fileConfig: {
        fileUrl: state.jwtToken, // JWT token
        mimetype: mimetype, // From File object
        fileName: filename, // Store the original filename
      },
    };
  }

  private async handleSuccessfulSave(ksId: string): Promise<void> {
    this.knowledgeCreated.emit(ksId);
    this.form.markAsPristine();
    this.updateFileState({ hasBeenSaved: true });

    if (!this.isOnLastStep()) {
      this.knowledgeSource = await this.knowledgeService.getKnowledgeSource(
        ksId,
        this.knowledgeService.buildNamespaceInterface(),
      );
      this.stepper.next();
    } else {
      this.close();
    }
  }

  private isFormValid(): boolean {
    // Use computed signal for pure validation
    if (this.isFormValidForCurrentStep()) {
      return true;
    }

    // Handle side effects for validation errors
    this.handleValidationSideEffects();
    return false;
  }

  private handleValidationSideEffects(): void {
    const currentStep = this.selectedStep();

    if (currentStep === 0) {
      this.markStepOneFieldsAsDirty();
      this.showValidationErrors();
    }
  }

  private markStepOneFieldsAsDirty(): void {
    const nameControl = this.form?.get('name');
    nameControl?.markAsDirty();
  }

  private showValidationErrors(): void {
    const errors = this.currentValidationErrors();

    // Show the first relevant error
    if (errors.length > 0) {
      // Prioritize name errors first, then file errors
      const hasValidName = this.formValiditySignal();
      if (!hasValidName) {
        // Name validation error - let form field handle display
        return;
      }

      // Show file-related errors via snackbar
      const fileErrors = errors.filter(
        (error) => error.includes('FILE_UPLOAD') || error.includes('UPLOADING') || error.includes('UPLOAD_PROCESSING'),
      );

      if (fileErrors.length > 0) {
        this.snackbarService.openErrorSnack(fileErrors[0]);
      }
    }
  }

  stepChanged(event: StepperSelectionEvent): void {
    this.selectedStep.set(event.selectedIndex);
    this.stepChange.emit(this.steps[this.selectedStep()]);
  }

  back(): void {
    if (this.shouldShowBack()) {
      this.stepper.previous();
    }
  }

  readonly isStepZeroComplete = computed(() => {
    // Check if step 0 has valid name and file (regardless of current step)
    const hasValidName = this.formValiditySignal();
    const hasValidFile = this.hasValidFile();

    return !!(hasValidName && hasValidFile);
  });

  async createWebChatWidget(): Promise<void> {
    await this.webChatWidgetService.createWebChatWidget({
      dialogRef: this.dialogRef,
      knowledgeSource: this.knowledgeSource,
      applicationSelector: this.applicationSelector,
    });
  }

  warnUnsavedChanged(func: () => void): void {
    if (!this.hasUnsavedChanges()) {
      func();
      return;
    }

    this.confirmationModal
      .openModal({
        type: 'warn',
        title: this.translateService.instant('UNSAVED_CHANGES.TITLE'),
        message: this.translateService.instant('UNSAVED_CHANGES.MESSAGE'),
        confirmButtonText: this.translateService.instant('COMMON.YES'),
        cancelButtonText: this.translateService.instant('COMMON.NO'),
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          func();
        }
      });
  }

  close(): void {
    this.warnUnsavedChanged(() => {
      this.stepChange.emit('');
      this.dialogRef.close();
    });
  }

  getCurrentFileName(): string {
    return this.currentFileInfo()?.fileName || 'Unknown file';
  }

  getCurrentFileType(): string {
    return this.currentFileInfo()?.displayType || '';
  }

  getFileIcon(): string {
    return this.currentFileInfo()?.icon || 'description';
  }

  getCurrentFileDownloadUrl(): string | null {
    return this.currentFileInfo()?.downloadUrl || null;
  }

  getCurrentFileViewUrl(): string | null {
    return this.currentFileInfo()?.viewUrl || null;
  }

  viewCurrentFile(): void {
    const fileInfo = this.currentFileInfo();
    if (fileInfo?.viewUrl) {
      window.open(fileInfo.viewUrl, '_blank');
    }
  }

  downloadCurrentFile(): void {
    const fileInfo = this.currentFileInfo();
    if (fileInfo?.downloadUrl) {
      const link = document.createElement('a');
      link.href = fileInfo.downloadUrl;
      link.download = fileInfo.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
